import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Zap, DollarSign, TrendingUp } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { JsonLd } from "@/components/JsonLd";
import { generateCanonicalUrl } from "@/utils/seoHelpers";

const GoogleAdsConroePage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  const canonicalUrl = generateCanonicalUrl('/google-ads-conroe');

  const content = {
    es: {
      title: "Google Ads Conroe TX | Campañas PPC Locales – Luis ROC",
      description: "Campañas Google Ads en Conroe, TX con segmentación local precisa. Demanda activada mediante campañas pagadas, medición orientada a negocio. Gestión profesional de Google Ads. Reunión inicial gratuita.",
      h1: "Google Ads en Conroe TX — Campañas orientadas a demanda local",
      intro: "Activa demanda local en Conroe con campañas Google Ads optimizadas por ZIP code. Segmentación precisa para 77301-77306. Gestión de Google Ads con control del gasto y transparencia en la medición.",
      keywords: "google ads conroe tx, ppc conroe texas, publicidad google conroe, campañas google ads conroe"
    },
    en: {
      title: "Google Ads Conroe TX | Local PPC Campaigns – Luis ROC",
      description: "Google Ads campaigns in Conroe, TX with precise local targeting. Demand captured through paid campaigns, business-focused measurement. Professional Google Ads management. free initial consultation.",
      h1: "Google Ads in Conroe TX — Campaigns focused on local demand",
      intro: "Activate local demand in Conroe with geographically focused Google Ads campaigns optimized by ZIP code. Precise targeting for 77301-77306. Measurable campaign structure and full metric transparency.",
      keywords: "google ads conroe tx, ppc conroe texas, google advertising conroe, google ads campaigns conroe"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": language === 'es' ? "Google Ads en Conroe TX" : "Google Ads in Conroe TX",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": {
      "@type": "City",
      "name": "Conroe",
      "containedIn": {
        "@type": "State",
        "name": "Texas"
      }
    },
    "description": content[language].description
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://luisroc.com" },
      { "@type": "ListItem", "position": 2, "name": "Servicios", "item": "https://luisroc.com/servicios" },
      { "@type": "ListItem", "position": 3, "name": "Google Ads Conroe", "item": canonicalUrl }
    ]
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{content[language].title}</title>
        <meta name="description" content={content[language].description} />
        <meta name="keywords" content={content[language].keywords} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Hreflang tags */}
        
        {/* Geo tags */}
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Conroe" />
        <meta name="geo.position" content="30.3119;-95.4560" />
        
        {/* Open Graph */}
        <meta property="og:title" content={content[language].title} />
        <meta property="og:description" content={content[language].description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content={language === 'es' ? 'es_US' : 'en_US'} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content[language].title} />
        <meta name="twitter:description" content={content[language].description} />
      </Helmet>

      <JsonLd data={serviceSchema} />
      <JsonLd data={breadcrumbSchema} />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main className="container mx-auto px-4 py-8">
          <BreadcrumbNav 
            items={[
              { name: language === 'es' ? "Servicios" : "Services", url: "/servicios" },
              { name: language === 'es' ? "Localidades" : "Locations", url: "/servicios-locales-houston" },
              { name: "Google Ads Conroe", url: "/google-ads-conroe" }
            ]}
            language={language}
          />

          <section className="py-16 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              {content[language].h1}
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              {content[language].intro}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => window.location.href = '/contacto'}>
                {language === 'es' ? 'Reunión inicial gratuita de 30 minutos' : 'Free 30-minute initial consultation'}
              </Button>
              <Button size="lg" variant="outline" onClick={() => window.location.href = '/google-ads-the-woodlands'}>
                {language === 'es' ? 'Ver Casos de Éxito' : 'View Success Cases'}
              </Button>
            </div>
          </section>

          <section className="py-16 bg-accent/5 rounded-lg mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">
              {language === 'es' ? 'Ventajas de Google Ads en Conroe' : 'Google Ads Benefits in Conroe'}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Zap className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold text-xl mb-3">
                  {language === 'es' ? 'Activación de demanda' : 'Demand activation'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'es' 
                    ? 'Las campañas pagadas permiten activar demanda mientras se mide el rendimiento desde el inicio.'
                    : 'Paid campaigns can activate demand while performance is measured from the start.'}
                </p>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <DollarSign className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold text-xl mb-3">
                  {language === 'es' ? 'Control total de presupuesto' : 'Total budget control'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'es'
                    ? 'Ajustamos pujas y presupuesto según tus metas.'
                    : 'We adjust bids and budget according to your goals.'}
                </p>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <TrendingUp className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold text-xl mb-3">
                  {language === 'es' ? 'medición orientada a negocio' : 'Business-focused measurement'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'es'
                    ? 'Tracking completo de conversiones y métricas.'
                    : 'Complete conversion and metrics tracking.'}
                </p>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Check className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold text-xl mb-3">
                  {language === 'es' ? 'Segmentación local' : 'Local targeting'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'es'
                    ? 'Anuncios solo a residentes de Conroe.'
                    : 'Ads only to Conroe residents.'}
                </p>
              </Card>
            </div>
          </section>

          <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              {language === 'es' ? 'Revisa tu estrategia de Google Ads en Conroe' : 'Review your Google Ads strategy in Conroe'}
            </h2>
            <p className="text-xl mb-6 opacity-90">
              {language === 'es' ? 'reunión inicial gratuita + setup de campaña' : 'free 30-minute initial consultation + campaign setup'}
            </p>
            <Button size="lg" variant="secondary" onClick={() => window.location.href = '/contacto'}>
              {language === 'es' ? 'Solicitar Consulta' : 'Request Consultation'}
            </Button>
          </section>

          <section className="py-8 text-center">
            <h3 className="text-xl font-semibold mb-4">
              {language === 'es' ? 'Google Ads en otras localidades' : 'Google Ads in other locations'}
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="outline" onClick={() => window.location.href = '/google-ads-the-woodlands'}>Google Ads The Woodlands</Button>
              <Button variant="outline" onClick={() => window.location.href = '/google-ads-houston'}>Google Ads Houston</Button>
              <Button variant="outline" onClick={() => window.location.href = '/google-ads-spring'}>Google Ads Spring</Button>
              <Button variant="outline" onClick={() => window.location.href = '/servicios-locales-houston'}>
                {language === 'es' ? 'Ver Todas' : 'View All'}
              </Button>
            </div>
          </section>
        </main>

        <KnowledgeConnections path="/google-ads-conroe" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default GoogleAdsConroePage;
